'use client'
import React, { createContext, useState, useEffect, useContext } from 'react';
import Bugsnag from '@bugsnag/js';
import { User, Installer, Role } from '@/app/types/global';
import { usePathname } from 'next/navigation';
import { cachedFetchUser } from '@/app/utils/utils';

type UserContextType = {
  user: User | null;
  installer: Installer | null;
  showStatus: boolean;
  setShowStatus: React.Dispatch<React.SetStateAction<boolean>>;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  pageSize: number;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
  setInstaller: React.Dispatch<React.SetStateAction<Installer | null>>;
  isInstaller: boolean;
  isAdmin: boolean;
  isHacker: boolean;
  isActive: boolean;
  isNone: boolean;
  isAnonymous: boolean;
};

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const pathName = usePathname();
  const [user, setUser] = useState<User | null>(null);
  const [installer, setInstaller] = useState<Installer | null>(null);
  const [redirected, setRedirected] = useState(false);
  const isInstaller = !!user?.role && ['installer', 'admin'].includes(user.role);
  const [showStatus, setShowStatus] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    const stored = localStorage.getItem('showStatus');
    return stored !== null ? JSON.parse(stored) : false;
  });
  const [pageSize, setPageSize] = useState<number>(() => {
    if (typeof window === 'undefined') return 25;
    const stored = localStorage.getItem('pageSize');
    return stored !== null ? JSON.parse(stored) : 25;
  });
  
  useEffect(() => {
    localStorage.setItem('showStatus', JSON.stringify(showStatus));
  }, [showStatus]);

  // Attach the current user to any future Bugsnag reports so we can see
  // who was affected when something goes wrong. setUser is silent — it
  // doesn't generate a report itself, only adds context to future ones.
  useEffect(() => {
    try {
      if (user) {
        Bugsnag.setUser(String(user.id), user.email, user.name);
      } else {
        Bugsnag.setUser(undefined, undefined, undefined);
      }
    } catch {
      // Bugsnag not started (e.g. in dev, or before init) — safe to ignore
    }
  }, [user]);

  const isAnonymous = !user;
  const isHacker = !!user?.role && ['installer', 'hacker', 'admin'].includes(user.role);
  const isAdmin = !!user?.role && ['admin'].includes(user.role);
  const isNone = !!user?.role && ['none'].includes(user.role);
  const isActive = !!user?.subscription_status && ['active'].includes(user.subscription_status);

  // Pages that don't require authentication
  const publicPages = ['/login', '/signup'];
  const isPublicPage = publicPages.some(p => pathName === p || pathName?.startsWith(p + '/'));
  if (isPublicPage) {
    return <>{children}</>;
  }

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const data = await cachedFetchUser();
        if (data) {
          if (data?.email) {
            setUser(data as User);
            const installerResponse = await fetch('/api/installer');
            if (installerResponse.ok) {
              setInstaller(await installerResponse.json());
            }
          } else {

            if (process.env.NODE_ENV !== 'development') {
              setRedirected(true);
              window.location.href = '/login';
            }
          }
        } else {

          if (process.env.NODE_ENV !== 'development') {
            setRedirected(true);
            window.location.href = '/login';
          }
        }
      } catch (error) {
        console.error('Error fetching user:', error);

        if (process.env.NODE_ENV !== 'development') {
          setRedirected(true);
          window.location.href = '/login';
        }
      }
    };
    
    if (!user && !redirected) {
      fetchUser();
    }
  }, [redirected]);

  // Allow anonymous render for public-ish pages
  if (pathName?.startsWith('/controller_health/') || pathName?.startsWith('/signup')) {
    return (
      <UserContext.Provider value={{
        user, installer,
        pageSize, setPageSize,
        showStatus, setShowStatus,
        setUser, setInstaller,
        isInstaller, isAdmin, isHacker, isActive, isNone, isAnonymous
      }}>
        {children}
      </UserContext.Provider>
    );
  }
  
  if (!user) {
    return null;
  }

  return (
    <UserContext.Provider value={{
      user, installer,
      pageSize, setPageSize,
      showStatus, setShowStatus,
      setUser, setInstaller,
      isInstaller, isAdmin, isHacker, isActive, isNone, isAnonymous
    }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

/**
 * Soft variant of useUser for components that may render on public pages
 * (login, signup) where UserProvider intentionally renders children
 * without a UserContext.Provider. Returns null instead of throwing so
 * layout-level UI (banners, etc.) can short-circuit gracefully.
 */
export const useOptionalUser = (): UserContextType | null => {
  const context = useContext(UserContext);
  return context ?? null;
};

/**
 * Phase 0 of doc/guidelines.md: collapse the overlapping booleans
 * (isInstaller, isAdmin, isHacker) into a single canonical role per
 * the three-role model. Precedence: admin > installer > customer.
 *
 * - 'hacker' is treated as 'installer' — hackers see device-level
 *   internals (Modbus, firmware) that installers need but admins
 *   exclusively see (algorithm internals, ROI, etc.) stay hidden.
 * - Anonymous users default to 'customer' so role-gated UI fails
 *   closed (hides installer/admin info) rather than open.
 */
export const useRole = (): Role => {
  const context = useContext(UserContext);
  if (!context) return 'customer';
  if (context.isAdmin) return 'admin';
  if (context.isInstaller || context.isHacker) return 'installer';
  return 'customer';
};
